import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { getPeople, type PersonRow } from '../lib/api';
import { formatDuration } from '../lib/format-duration';
import {
  AKHARA_BG,
  AKHARA_TEXT,
  MITTI_BG,
  MITTI_TEXT,
  filterPeople,
  isSortKey,
  naturalDir,
  relativeAgo,
  rosterTiles,
  sortPeople,
  typeCounts,
  type SortDir,
  type SortKey,
  type TypeFilter,
} from '../lib/people-time';

// People roster: who is putting in the work, where they are on the path, who has gone quiet.
// RosterView is presentational (plain props, no fetching); AdminRoster loads and wraps it.

/** Stacked Akhara + Mitti bar scaled against `max` minutes. */
export function TimeBar({ akhara, mitti, max }: { akhara: number; mitti: number; max: number }) {
  const w = (v: number) => (max > 0 ? `${Math.min(100, (v / max) * 100)}%` : '0%');
  return (
    <div
      className="flex h-2 w-full overflow-hidden rounded-full bg-paper-100 dark:bg-clay-raised"
      role="img"
      aria-label={`Akhara ${formatDuration(akhara)}, Mitti ${formatDuration(mitti)}`}
    >
      <div className={AKHARA_BG} style={{ width: w(akhara) }} />
      <div className={MITTI_BG} style={{ width: w(mitti) }} />
    </div>
  );
}

export function Legend() {
  return (
    <span className="inline-flex items-center gap-3 text-xs text-ink-500 dark:text-paper-300">
      <span className="inline-flex items-center gap-1">
        <span className={`h-2.5 w-2.5 rounded-sm ${AKHARA_BG}`} /> Akhara
      </span>
      <span className="inline-flex items-center gap-1">
        <span className={`h-2.5 w-2.5 rounded-sm ${MITTI_BG}`} /> Mitti
      </span>
    </span>
  );
}

export function Tile({
  label,
  value,
  note,
  title,
}: {
  label: string;
  value: React.ReactNode;
  note?: React.ReactNode;
  title?: string;
}) {
  return (
    <div
      title={title}
      className="rounded-xl border border-rule bg-paper-0 px-3 py-2.5 dark:border-clay-rule dark:bg-clay-surface"
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-ink-400">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums text-ink-900 dark:text-paper-50">{value}</p>
      {note && <p className="mt-0.5 text-xs text-ink-500 dark:text-paper-300">{note}</p>}
    </div>
  );
}

export function TypeChip({ type }: { type: PersonRow['type'] }) {
  return (
    <span
      className={`rounded-full px-1.5 py-px text-[10px] font-medium ${
        type === 'team'
          ? 'bg-paper-100 text-ink-600 dark:bg-clay-raised dark:text-paper-300'
          : 'border border-rule text-ink-600 dark:border-clay-rule dark:text-paper-300'
      }`}
    >
      {type === 'team' ? 'Team' : 'Candidate'}
    </span>
  );
}

export function PausedChip({ paused }: { paused: PersonRow['paused'] }) {
  if (!paused) return null;
  return (
    <span
      title="Access paused after inactivity. Un-revoke their invite on the Invites tab to bring them back."
      className="rounded-full border border-rule px-1.5 py-px text-[10px] font-medium text-ink-500 dark:border-clay-rule dark:text-paper-300"
    >
      Paused
    </span>
  );
}

export function CertDots({ certs }: { certs: PersonRow['certs'] }) {
  const items: Array<[string, boolean, string]> = [
    ['F', certs.foundations, 'Foundations'],
    ['C', certs.configurator, 'Configurator'],
    // Only shown for people who have been given the Architecture course.
    ...(certs.architecture === undefined ? [] : [['A', certs.architecture, 'Architecture'] as [string, boolean, string]]),
    ['I', certs.irm, 'IRM'],
  ];
  return (
    <span className="inline-flex gap-1">
      {items.map(([l, ok, name]) => (
        <span
          key={l}
          title={`${name} certification: ${ok ? 'earned' : 'not yet'}`}
          aria-label={`${name} ${ok ? 'earned' : 'not yet'}`}
          className={`flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold ${
            ok
              ? 'bg-ink-700 text-white dark:bg-paper-200 dark:text-ink-900'
              : 'border border-rule text-ink-300 dark:border-clay-rule'
          }`}
        >
          {l}
        </span>
      ))}
    </span>
  );
}

export function StageCell({ stage }: { stage: PersonRow['stage'] }) {
  if (!stage) return <span className="text-ink-400">Nothing assigned</span>;
  const pct = stage.total > 0 ? Math.round((stage.done / stage.total) * 100) : 0;
  return (
    <div className="min-w-0 max-w-[18rem]">
      <p className="truncate text-xs text-ink-700 dark:text-paper-200" title={stage.title}>{stage.title}</p>
      <div className="mt-1 flex items-center gap-2">
        <div className="h-1 w-20 overflow-hidden rounded-full bg-paper-200 dark:bg-clay-rule">
          <div className="h-1 rounded-full bg-ink-700 dark:bg-paper-200" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[11px] tabular-nums text-ink-400">
          {stage.done} of {stage.total}
        </span>
      </div>
    </div>
  );
}

function MittiChip({ m }: { m: PersonRow['mitti'] }) {
  if (!m) return <span className="text-ink-400">—</span>;
  return (
    <span className="rounded-full bg-paper-100 px-2 py-0.5 text-[11px] font-medium text-ink-700 dark:bg-clay-raised dark:text-paper-200">
      {m.state}
    </span>
  );
}

const SORTS: Array<[SortKey, string]> = [
  ['week', 'Last 7 days'],
  ['allTime', 'All time'],
  ['zor', 'Zor'],
  ['lastActive', 'Last active'],
  ['name', 'Name'],
  ['stage', 'Stage'],
  ['certs', 'Certs'],
  ['mitti', 'Mitti'],
];

function SortTh({
  k,
  label,
  title,
  sort,
  dir,
  onSort,
  right,
}: {
  k: SortKey;
  label: string;
  title: string;
  sort: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  right?: boolean;
}) {
  const active = sort === k;
  return (
    <th
      className={`${TH} !p-0 ${right ? 'text-right' : ''}`}
      title={title}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`group flex w-full items-center gap-1 px-3 py-2 font-medium uppercase tracking-wide hover:text-ink-900 dark:hover:text-paper-50 ${
          right ? 'justify-end' : ''
        }`}
      >
        {label}
        {active ? (
          dir === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          )
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-0 group-hover:opacity-40" aria-hidden />
        )}
      </button>
    </th>
  );
}

const INCOMPLETE = "Couldn't load this person's data. Reload to try again.";
const TH = 'whitespace-nowrap px-3 py-2 font-medium';

export function RosterView({
  people,
  weekStart,
  today,
  now,
  truncated,
  onOpen,
  sort: sortProp = 'week',
  dir: dirProp = 'desc',
  onSortChange,
}: {
  people: PersonRow[];
  weekStart: string;
  today: string;
  now?: number;
  truncated?: boolean;
  /** Called with a uid when a desktop row is clicked; the name stays a real link. */
  onOpen?: (uid: string) => void;
  /** Sort state; the wrapper keeps it in the URL. Without a handler the view sorts locally. */
  sort?: SortKey;
  dir?: SortDir;
  onSortChange?: (sort: SortKey, dir: SortDir) => void;
}) {
  const [type, setType] = useState<TypeFilter>('all');
  const [search, setSearch] = useState('');
  const [localSort, setLocalSort] = useState<{ sort: SortKey; dir: SortDir }>({ sort: sortProp, dir: dirProp });
  // Carried to the person page so its back link returns to this sorted view.
  const loc = useLocation();
  const sort = onSortChange ? sortProp : localSort.sort;
  const dir = onSortChange ? dirProp : localSort.dir;
  const applySort = (k: SortKey, d: SortDir) => (onSortChange ? onSortChange(k, d) : setLocalSort({ sort: k, dir: d }));
  const onHeader = (k: SortKey) => applySort(k, k === sort ? (dir === 'asc' ? 'desc' : 'asc') : naturalDir(k));
  const t = now ?? Date.now();

  const tiles = useMemo(() => rosterTiles(people.filter((p) => !p.incomplete), t), [people, t]);
  const counts = useMemo(() => typeCounts(people), [people]);
  const rows = useMemo(() => sortPeople(filterPeople(people, type, search), sort, dir), [people, type, search, sort, dir]);
  const maxWeek = Math.max(0, ...rows.map((p) => p.week.akharaMin + p.week.mittiMin));

  const filters: Array<[TypeFilter, string, number]> = [
    ['all', 'All', counts.all],
    ['team', 'Team', counts.team],
    ['candidate', 'Candidates', counts.candidate],
  ];

  return (
    <div>
      <p className="text-sm text-ink-500 dark:text-paper-300">
        Who is putting in the work, where they are on the path, and who has gone quiet.
      </p>
      <p className="mt-1 text-xs text-ink-400">
        Time is counted in active 5-minute windows: Akhara when the tab is open and the person is
        doing something, Mitti when their practice site handled a signed-in call. The last 7 days
        run {weekStart} to {today} (IST).
      </p>

      {truncated && (
        <p className="mt-2 text-xs text-terracotta-700" role="status">
          Showing 2,000 people only; some people are not listed.
        </p>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="Active this week"
          value={
            <>
              {tiles.active} <span className="text-sm font-normal text-ink-400">of {tiles.total}</span>
            </>
          }
          note="People with any time in the last 7 days"
        />
        <Tile
          label="Hours this week"
          value={formatDuration(tiles.akharaMin + tiles.mittiMin)}
          note={
            <>
              <span className={AKHARA_TEXT}>Akhara {formatDuration(tiles.akharaMin)}</span> ·{' '}
              <span className={MITTI_TEXT}>Mitti {formatDuration(tiles.mittiMin)}</span>
            </>
          }
        />
        <Tile label="Steps this week" value={tiles.steps} note="Paintras completed, last 7 days" />
        <Tile
          label="Quiet for 7 days"
          value={tiles.quiet}
          note="Have something assigned, no activity in 7 days"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div
          role="group"
          aria-label="Filter by type"
          className="flex gap-0.5 rounded-lg border border-rule bg-paper-0 p-0.5 text-sm dark:border-clay-rule dark:bg-clay-surface"
        >
          {filters.map(([k, label, c]) => (
            <button
              key={k}
              onClick={() => setType(k)}
              aria-pressed={type === k}
              className={`rounded-md px-3 py-1 font-medium transition ${
                type === k ? 'bg-terracotta-500 text-white' : 'text-ink-500 hover:text-ink-900 dark:text-paper-300'
              }`}
            >
              {label} <span className="tabular-nums opacity-80">{c}</span>
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email"
          aria-label="Search people by name or email"
          className="min-w-[10rem] flex-1 rounded-lg border border-rule bg-paper-0 px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-300 dark:border-clay-rule dark:bg-clay-surface dark:text-paper-50"
        />
        <div className="flex items-center gap-1.5 text-xs text-ink-500 sm:hidden dark:text-paper-300">
          <label className="flex items-center gap-1.5">
          Sort
          <select
            value={sort}
            onChange={(e) => applySort(e.target.value as SortKey, naturalDir(e.target.value as SortKey))}
            className="rounded-lg border border-rule bg-paper-0 px-2 py-1.5 text-sm text-ink-900 dark:border-clay-rule dark:bg-clay-surface dark:text-paper-50"
          >
            {SORTS.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
          </label>
          <button
            type="button"
            onClick={() => applySort(sort, dir === 'asc' ? 'desc' : 'asc')}
            aria-label={dir === 'asc' ? 'Ascending; switch to descending' : 'Descending; switch to ascending'}
            className="rounded-lg border border-rule bg-paper-0 p-1.5 text-ink-700 dark:border-clay-rule dark:bg-clay-surface dark:text-paper-200"
          >
            {dir === 'asc' ? <ArrowUpNarrowWide className="h-4 w-4" /> : <ArrowDownWideNarrow className="h-4 w-4" />}
          </button>
        </div>
        <Legend />
      </div>

      {rows.length === 0 ? (
        <div className="mt-4 rounded-xl border border-rule bg-paper-0 px-4 py-8 text-center text-sm text-ink-500 dark:border-clay-rule dark:bg-clay-surface dark:text-paper-300">
          {people.length === 0 ? 'No people yet. Invite someone from the Invites tab.' : 'No one matches.'}
        </div>
      ) : (
        <>
          {/* Table from sm up */}
          <div className="mt-3 hidden overflow-x-auto rounded-xl border border-rule sm:block dark:border-clay-rule">
            <table className="w-full whitespace-nowrap text-sm">
              <thead className="bg-paper-100 text-left text-xs uppercase tracking-wide text-ink-400 dark:bg-clay-raised/40">
                <tr>
                  <SortTh k="name" label="Person" title="Name, email and whether they are on the team or a candidate" sort={sort} dir={dir} onSort={onHeader} />
                  <SortTh k="stage" label="Stage" title="The first path track not yet finished, with steps done of total" sort={sort} dir={dir} onSort={onHeader} />
                  <SortTh k="certs" label="Certs" title="Certifications earned: Foundations, Configurator, Architecture, IRM" sort={sort} dir={dir} onSort={onHeader} />
                  <SortTh k="week" label="Last 7 days" title="Active time in the last 7 days: Akhara (terracotta) and Mitti (green)" sort={sort} dir={dir} onSort={onHeader} />
                  <SortTh k="allTime" label="All time" title="Active time since they joined, Akhara plus Mitti" sort={sort} dir={dir} onSort={onHeader} right />
                  <SortTh k="zor" label="Zor" title="Zor earned, all time" sort={sort} dir={dir} onSort={onHeader} right />
                  <SortTh k="lastActive" label="Last active" title="When they last did anything in Akhara or on their Mitti" sort={sort} dir={dir} onSort={onHeader} />
                  <SortTh k="mitti" label="Mitti" title="State of their current Mitti (practice site)" sort={sort} dir={dir} onSort={onHeader} />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const wk = p.week.akharaMin + p.week.mittiMin;
                  const bad = p.incomplete === true;
                  return (
                    <tr
                      key={p.uid}
                      onClick={onOpen ? () => onOpen(p.uid) : undefined}
                      className={`border-t border-rule hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised/40 ${
                        onOpen ? 'cursor-pointer' : ''
                      }`}
                    >
                      <td className="px-3 py-2">
                        <Link to={`/admin/people/${encodeURIComponent(p.uid)}`} state={{ from: loc.search }} className="block">
                          <span className="flex items-center gap-2">
                            <span className="font-medium text-ink-900 dark:text-paper-50">
                              {p.displayName || p.email}
                            </span>
                            <TypeChip type={p.type} />
                            <PausedChip paused={p.paused} />
                          </span>
                          <span className="block text-xs text-ink-400">{p.email}</span>
                        </Link>
                      </td>
                      <td className="px-3 py-2" title={bad ? INCOMPLETE : undefined}>
                        {bad ? <span className="text-ink-400">—</span> : <StageCell stage={p.stage} />}
                      </td>
                      <td className="px-3 py-2" title={bad ? INCOMPLETE : undefined}>
                        {bad ? <span className="text-ink-400">—</span> : <CertDots certs={p.certs} />}
                      </td>
                      <td className="px-3 py-2" title={bad ? INCOMPLETE : undefined}>
                        {bad ? (
                          <span className="text-ink-400">—</span>
                        ) : (
                          <div className="w-32">
                            <TimeBar akhara={p.week.akharaMin} mitti={p.week.mittiMin} max={maxWeek} />
                            <p className="mt-0.5 text-xs tabular-nums text-ink-700 dark:text-paper-200">
                              {formatDuration(wk)}
                            </p>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums" title={bad ? INCOMPLETE : undefined}>
                        {bad ? '—' : formatDuration(p.allTime.akharaMin + p.allTime.mittiMin)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-ink-900 dark:text-paper-50">
                        {p.allTime.zor}
                      </td>
                      <td className="px-3 py-2 text-ink-500 dark:text-paper-300">{relativeAgo(p.lastActiveAt, t)}</td>
                      <td className="px-3 py-2">
                        <MittiChip m={p.mitti} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Cards below sm */}
          <div className="mt-3 flex flex-col gap-3 sm:hidden">
            {rows.map((p) => {
              const wk = p.week.akharaMin + p.week.mittiMin;
              const bad = p.incomplete === true;
              return (
                <Link
                  key={p.uid}
                  to={`/admin/people/${encodeURIComponent(p.uid)}`}
                  state={{ from: loc.search }}
                  className="block rounded-xl border border-rule bg-paper-0 p-3 dark:border-clay-rule dark:bg-clay-surface"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 font-semibold text-ink-900 dark:text-paper-50">
                        <span className="truncate">{p.displayName || p.email}</span>
                        <TypeChip type={p.type} />
                        <PausedChip paused={p.paused} />
                      </p>
                      <p className="truncate text-xs text-ink-400">{p.email}</p>
                    </div>
                    {!bad && <CertDots certs={p.certs} />}
                  </div>
                  {bad ? (
                    <p className="mt-2 text-xs text-ink-400" title={INCOMPLETE}>
                      — {INCOMPLETE}
                    </p>
                  ) : (
                    <>
                      <div className="mt-2">
                        <StageCell stage={p.stage} />
                      </div>
                      <div className="mt-2">
                        <TimeBar akhara={p.week.akharaMin} mitti={p.week.mittiMin} max={maxWeek} />
                        <p className="mt-0.5 text-xs text-ink-500 dark:text-paper-300">
                          {formatDuration(wk)} in 7 days · {formatDuration(p.allTime.akharaMin + p.allTime.mittiMin)} all
                          time · {p.allTime.zor} Zor
                        </p>
                      </div>
                    </>
                  )}
                  <p className="mt-1 flex items-center gap-2 text-xs text-ink-400">
                    Last active {relativeAgo(p.lastActiveAt, t)}
                    {p.mitti && <MittiChip m={p.mitti} />}
                  </p>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'forbidden' }
  | { phase: 'ready'; people: PersonRow[]; weekStart: string; today: string; truncated: boolean };

export default function AdminRoster() {
  const location = useLocation();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [tick, setTick] = useState(0);
  const navigate = useNavigate();
  const forbiddenRef = useRef(false);
  const reload = useCallback(() => {
    if (!forbiddenRef.current) setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (forbiddenRef.current) return;
    let cancelled = false;
    setState({ phase: 'loading' });
    void (async () => {
      const res = await getPeople();
      if (cancelled || forbiddenRef.current) return;
      if (res.status === 403) {
        forbiddenRef.current = true;
        setState({ phase: 'forbidden' });
        return;
      }
      if (!res.ok) {
        setState({
          phase: 'error',
          message: res.status === 0 ? 'Network error — check your connection.' : `Failed (HTTP ${res.status}).`,
        });
        return;
      }
      setState({
        phase: 'ready',
        people: Array.isArray(res.data?.people) ? res.data.people : [],
        weekStart: res.data?.weekStart ?? '',
        today: res.data?.today ?? '',
        truncated: res.data?.truncated === true,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const [params, setParams] = useSearchParams();
  const qSort = params.get('sort');
  const sort: SortKey = isSortKey(qSort) ? qSort : 'week';
  const qDir = params.get('dir');
  const dir: SortDir = qDir === 'asc' || qDir === 'desc' ? qDir : isSortKey(qSort) ? naturalDir(qSort) : 'desc';
  const onSortChange = (k: SortKey, d: SortDir) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('sort', k);
        next.set('dir', d);
        return next;
      },
      { replace: true },
    );

  return (
    <div>
      {state.phase === 'forbidden' && (
        <div className="rounded-xl border border-warn-500/40 bg-paper-0 p-4 text-sm dark:bg-clay-surface" role="alert">
          <p className="font-semibold text-ink-900 dark:text-paper-50">Not authorized</p>
          <p className="mt-1 text-ink-500 dark:text-paper-300">This view is for Khalifas (admins) only.</p>
        </div>
      )}
      {state.phase === 'loading' && <p className="text-sm text-ink-400">Loading…</p>}
      {state.phase === 'error' && (
        <div className="text-sm text-terracotta-700">
          {state.message}{' '}
          <button onClick={reload} className="underline">
            Retry
          </button>
        </div>
      )}
      {state.phase === 'ready' && (
        <>
          <div className="mb-2 flex justify-end">
            <button
              onClick={reload}
              className="rounded-lg border border-rule px-2.5 py-1 text-xs font-medium text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised"
            >
              Refresh
            </button>
          </div>
          <RosterView
            people={state.people}
            weekStart={state.weekStart}
            today={state.today}
            truncated={state.truncated}
            sort={sort}
            dir={dir}
            onSortChange={onSortChange}
            onOpen={(uid) => navigate(`/admin/people/${encodeURIComponent(uid)}`, { state: { from: location.search } })}
          />
        </>
      )}
    </div>
  );
}
